import { reportReliabilityFailure } from './webFailureReporting'
import {
  isWebErrorReported,
  leaveWebBreadcrumb,
  markWebErrorReported,
  notifyWebError,
} from './webErrorReporting'

const BUXUS_STORAGE_FAILURE = Symbol.for('buxus.storageFailure')

/** True when this error was produced by IndexedDB / localStorage reporters. */
export function isBuxusStorageFailure(error: unknown): boolean {
  return (
    error !== null &&
    typeof error === 'object' &&
    (error as Record<symbol, unknown>)[BUXUS_STORAGE_FAILURE] === true
  )
}

function markBuxusStorageFailure(error: unknown): void {
  if (error !== null && typeof error === 'object') {
    Object.defineProperty(error, BUXUS_STORAGE_FAILURE, {
      value: true,
      enumerable: false,
      configurable: true,
    })
  }
}

export type IndexedDbStoreName = 'operator-files' | 'operator-payloads'

export type IndexedDbOperationMeta = {
  store: IndexedDbStoreName
  dbName: string
  operation:
    | 'open'
    | 'putFile'
    | 'getFile'
    | 'hasFile'
    | 'pruneFiles'
    | 'deleteFiles'
    | 'clearFiles'
    | 'putBase'
    | 'putStakeholder'
    | 'getBase'
    | 'getStakeholder'
    | 'listStakeholders'
    | 'clearPayloads'
    | 'deletePayloads'
  operatorId?: string
  fileId?: string
  stakeholderCode?: string
  version?: string
  blobSizeBytes?: number
  /**
   * When true, the caller treated this as non-blocking (e.g. manifest file cache
   * during operator download) and the user-facing download can still succeed.
   */
  ignored?: boolean
}

/** Prefer request.error; Safari often leaves `transaction.error` null on put failures. */
export function resolveIdbDomError(
  ...errors: Array<DOMException | null | undefined>
): DOMException | null {
  for (const error of errors) {
    if (error) return error
  }
  return null
}

function domExceptionDetail(error: unknown): Record<string, unknown> {
  const candidates: unknown[] = [error]
  if (error instanceof Error && 'cause' in error && error.cause !== undefined) {
    candidates.push(error.cause)
  }
  for (const candidate of candidates) {
    if (candidate instanceof DOMException) {
      return {
        domExceptionName: candidate.name,
        domExceptionMessage: candidate.message,
      }
    }
  }
  return {}
}

function storageError(error: unknown, fallbackMessage: string): Error {
  if (error instanceof DOMException) {
    if (error.message?.trim()) return error
    return new Error(`${fallbackMessage}: ${error.name}`, { cause: error })
  }
  if (error instanceof Error) {
    if (error.message?.trim()) return error
    return new Error(fallbackMessage, { cause: error })
  }
  if (typeof error === 'string' && error.trim()) return new Error(error)
  return new Error(fallbackMessage, { cause: error ?? undefined })
}

export function reportIndexedDbError(error: unknown, meta: IndexedDbOperationMeta): void {
  if (isWebErrorReported(error)) return

  const err = storageError(error, `IndexedDB ${meta.operation} failed`)
  const detail = domExceptionDetail(error)

  leaveWebBreadcrumb(
    'indexedDB error',
    {
      ...meta,
      ...detail,
      message: err.message,
      ...(meta.ignored ? { ignored: true } : {}),
    },
    'error',
  )

  markBuxusStorageFailure(err)
  if (error !== err) markBuxusStorageFailure(error)

  // Best-effort per-file cache miss: breadcrumb only. Download call sites emit one
  // Bugsnag + Mixpanel summary from executeOperatorDownload failure counts.
  if (meta.ignored) {
    markWebErrorReported(err)
    if (error !== err) markWebErrorReported(error)
    return
  }

  reportReliabilityFailure(err, {
    context: 'indexedDB',
    metadata: {
      storage: {
        kind: 'indexedDB',
        ...meta,
        ...detail,
      },
    },
    analyticsProps: {
      store: meta.store,
      dbName: meta.dbName,
      operation: meta.operation,
      ...detail,
      ...(meta.operatorId ? { operatorId: meta.operatorId } : {}),
      ...(meta.fileId ? { fileId: meta.fileId } : {}),
      ...(meta.stakeholderCode ? { stakeholderCode: meta.stakeholderCode } : {}),
      ...(meta.version ? { version: meta.version } : {}),
      ...(meta.blobSizeBytes != null ? { blobSizeBytes: meta.blobSizeBytes } : {}),
    },
  })
}

/** Normalize, report (Bugsnag + Mixpanel), and return an Error suitable for rejecting IDB promises. */
export function toIndexedDbError(
  error: unknown,
  fallbackMessage: string,
  meta: IndexedDbOperationMeta,
): Error {
  const message = fallbackMessage.trim()
    ? fallbackMessage
    : `IndexedDB ${meta.operation} failed`
  const err = storageError(error, message.includes(meta.operation) ? message : `IndexedDB ${meta.operation} failed`)
  reportIndexedDbError(err, meta)
  return err
}

/**
 * Readwrite transaction helper: listens to request + transaction errors and avoids double-reject.
 * Use for put/clear-style ops where Safari may only populate `request.error`.
 */
export function idbReadwritePromise(
  db: IDBDatabase,
  storeName: string,
  meta: IndexedDbOperationMeta,
  fallbackMessage: string,
  execute: (store: IDBObjectStore) => IDBRequest,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite')
    let settled = false
    let req: IDBRequest | undefined

    const fail = () => {
      if (settled) return
      settled = true
      reject(
        toIndexedDbError(
          resolveIdbDomError(req?.error, tx.error),
          fallbackMessage,
          meta,
        ),
      )
    }

    tx.oncomplete = () => {
      if (settled) return
      settled = true
      resolve()
    }
    tx.onerror = fail
    tx.onabort = fail

    req = execute(tx.objectStore(storeName))
    req.onerror = fail
  })
}

export type LocalStorageOperationMeta = {
  operation: 'setItem' | 'getItem' | 'removeItem'
  key: string
}

export function reportLocalStorageError(error: unknown, meta: LocalStorageOperationMeta): void {
  if (isWebErrorReported(error)) return

  const err = storageError(error, `localStorage ${meta.operation} failed`)
  const detail = domExceptionDetail(err)

  leaveWebBreadcrumb(
    'localStorage error',
    {
      ...meta,
      ...detail,
      message: err.message,
    },
    'error',
  )

  markBuxusStorageFailure(err)
  if (error !== err) markBuxusStorageFailure(error)

  notifyWebError(err, {
    context: 'localStorage',
    severity: 'warning',
    metadata: {
      storage: {
        kind: 'localStorage',
        ...meta,
        ...detail,
      },
    },
  })
  markWebErrorReported(err)
  if (error !== err) {
    markWebErrorReported(error)
  }
}
